import type { WorkflowItem } from "./types";

interface PackageMilestone {
  id: string;
  sequence_no: number;
  name: string;
  actual?: string | null;
  actual_date?: string | null;
}

/**
 * Adapter that derives the flattened `WorkflowItem` shape from a bid/design
 * package grid row (which carries a raw `milestones` JSON array instead of the
 * server-flattened fields materials get). Reuses the single-item next-milestone
 * algorithm from `FeatureLogSetStatusUI`: sort by sequence_no, next = first
 * without an actual date, previous = the one before it.
 */
export function mapPackageRowToWorkflowItem(row: any): WorkflowItem {
  const milestones: PackageMilestone[] = (row?.milestones ?? [])
    .slice()
    .sort(
      (a: PackageMilestone, b: PackageMilestone) =>
        a.sequence_no - b.sequence_no
    );

  const next = milestones.find((m) => !m.actual_date && !m.actual);

  let previousMilestoneDate: string | null = null;
  if (next && next.sequence_no > 1) {
    const prev = milestones.find((m) => m.sequence_no === next.sequence_no - 1);
    previousMilestoneDate = prev?.actual_date ?? prev?.actual ?? null;
  }

  return {
    id: row.id,
    title: `${row.auto_inc_value ?? row.number ?? ""} - ${row.title ?? ""}`,
    workflow_status: row.workflow_status,
    date_block: { id: row.feature_workflow_instance_id },
    current_milestone_id: next?.id,
    current_milestone_name: next?.name,
    previous_milestone_date: previousMilestoneDate,
    first_milestone_id: milestones[0]?.id,
    first_milestone_name: milestones[0]?.name,
    // bid rows carry a template name; design rows have none -> "" (header suppressed)
    template: { name: row.bid_package_template_name ?? "" },
    ready_for_release: row.ready_for_release,
    // The list row has no predecessor array; ready_for_release is the server's
    // verdict on the linked design packages, so its presence asserts the answer.
    has_predecessors: row.ready_for_release != null,
    linked_items: [],
    materials: []
  };
}
